import React, { useState } from 'react';
import DateField from '../connectedForm/DateInput/DateInput';
import Label from '../connectedForm/Label/Label';
import CheckboxInput from '../connectedForm/CheckboxInput/CheckboxInput';
import {
  IGenerateNominalTransactionReportMutationResult,
  IGenerateNominalTransactionReportMutationVariables,
  IReportTemplate,
  ReportOutputType,
  ReportType,
} from '../../graphql/schema/p4types';
import { BasicHeader } from '../../actions/BasicHeader';
import Dropdown from '../connectedForm/Dropdown';
import { getDefaultOutputType } from '../../utils/getDefaultOutputType';
import { BasicOutputSelection } from './BasicOutputSelection';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useReportTemplatesQuery } from '../../utils/hooks';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useCheckPopUpBlockerFunction } from '../../utils/CheckPopUpBlockerFunction';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import GenerateNominalTransactionReportMutation from '../../graphql/mutations/nominal/GenerateNominalTransactionReportMutation.graphql';
import { useActionCriteria } from '../../utils/useActionProps';
import { ModalBottomButtonContainer } from './Styles';
import { Button, SuccessButton } from '../connectedForm/Button';
import FluentModal from './FluentModal';

const NominalTransactionCriteriaModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [template, setTemplate] = useState<Maybe<IReportTemplate>>();
  const [outputType, setOutputType] = useState<Maybe<{ value: ReportOutputType; label: string }>>();
  const [documentFromDate, setDocumentFromDate] = useState();
  const [documentToDate, setDocumentToDate] = useState();
  const [showOpeningClosing, setShowOpeningClosing] = useState(false);

  const checkPopUpBlockerFunction = useCheckPopUpBlockerFunction();
  const [generateAccountTransactions] = useBackgroundMutation<
    IGenerateNominalTransactionReportMutationResult,
    IGenerateNominalTransactionReportMutationVariables
  >(GenerateNominalTransactionReportMutation, {
    onCompleteJob: (job) => {
      if (job.properties.reportFile) {
        const newTab = checkPopUpBlockerFunction(`/api/resources/report/${job.properties.reportFile}`, '_blank');
        newTab?.print();
      }
      onClose();
    },
  });

  const { templates } = useReportTemplatesQuery(ReportType.NominalTransactionReport);

  const actionCriteria = useActionCriteria();
  const onPrint = () => {
    generateAccountTransactions({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          reportParameters: {
            template: normalizeVariables(template ?? null),
            outputType: outputType?.value ?? null,
          },
          nominalTransactionReportCriteria: {
            actionCriteria: actionCriteria,
            documentFromDate: documentFromDate,
            documentToDate: documentToDate,
            showOpeningClosing: showOpeningClosing,
          },
        },
      },
    });
  };

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { paddingBottom: '60px', minWidth: '700px', maxWidth: '1000px' } }}>
      <BasicHeader>Nominal Account Transactions</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Dropdown
          data-testid="account-transactions-template"
          options={templates}
          valueKey="id"
          labelKey="name"
          onChange={(newTemplate) => {
            setTemplate(newTemplate);
            setOutputType(getDefaultOutputType(newTemplate?.reportTemplateFormat));
          }}
          label="Template"
          value={template}
        />
        <Dropdown
          data-testid="account-transactions-output"
          options={BasicOutputSelection}
          label="Output"
          onChange={setOutputType}
          value={outputType}
          valueKey="value"
          labelKey="label"
          clearable={false}
        />
        <Label>Document dates</Label>
        <DateField
          data-testid="account-transactions-document-from"
          label="From"
          portal={true}
          value={documentFromDate}
          onChange={setDocumentFromDate}
          mandatory={true}
        />
        <DateField
          data-testid="account-transactions-document-to"
          label="To"
          portal={true}
          value={documentToDate}
          onChange={setDocumentToDate}
          mandatory={true}
        />
        <CheckboxInput
          data-testid="account-transactions-show-opening-closing"
          value={showOpeningClosing}
          onChange={() => setShowOpeningClosing((prevShowOpeningClosing) => !prevShowOpeningClosing)}
          label="Include opening/closing balance"
        />
      </div>
      <ModalBottomButtonContainer>
        <Button data-testid="nominal-account-transactions-cancel" onClick={onClose}>
          Cancel
        </Button>
        <SuccessButton data-testid="nominal-account-transactions-print" onClick={onPrint}>
          Print
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default NominalTransactionCriteriaModal;
